import React from "react";
import { useTheme } from "react-jss";
import classnames from "classnames";
import useStyles from "./RadioRenderer.styles";
import { RendererProps } from "../BaseRenderer";
import { ChoiceRef } from "../../choices/BaseChoice";
import { ChoiceModel } from "../../../models/ChoiceModel";
import { RadioIcon } from "../../shared/RadioIcon";
import { translate } from "../../../models/utilities/translationUtilities";
import { removeHtmlTags } from "../../../utils";
import useQuestion from "../../../hooks/useQuestion";
import {
    getAriaLabelledByFromId,
    useAriaLabelledByForInputFieldQuestion,
} from "../../../utils/WCAGHelper";
import { QuestionType } from "../../../models/enums/QuestionType";
import { useKeyboardNavigation } from "../../../hooks/useKeyboardNavigation";
import { ChoiceGridRendererNavigation } from "../../controls/KeyboardNavigation/ChoiceGridRendererNavigation";

interface Props extends RendererProps {
    choice: ChoiceModel;
    name?: string;
    value?: string;
    onSelect?(): void;
    checked?: boolean;
    disabled?: boolean;
}

const RadioRenderer = React.forwardRef(function RadioRendererImpl(props: Props, ref: ChoiceRef) {
    const {
        choice,
        checked,
        gridRendering,
        onSelect,
        variable,
        disabled,
        language,
        gridRenderedColumnIndex,
        gridRenderedRowIndex,
    } = props;

    const [hover, setHover] = React.useState(false);
    const theme: any = useTheme();
    const classes = useStyles({ theme });

    React.useImperativeHandle(ref, () => ({
        select() {
            onSelect();
        },
    }));

    if (!gridRendering) {
        return <RadioIcon checked={checked} disabled={disabled} backgroundLess={gridRendering} />;
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const question = useQuestion();
    const questionId = question?.parent?.elementId ?? question?.elementId;
    const questionType = question?.parent?.type ?? question?.type;

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const useAriaLabelledBy = useAriaLabelledByForInputFieldQuestion(question);

    // Default should be aria-label if not grid rendered and aria-labelled by for variable type in grid
    const useAriaLabel = !useAriaLabelledBy && !gridRendering;
    const ariaAttributes = {};
    const isChoiceOrHybridGridType =
        questionType === QuestionType.ChoiceGrid ||
        questionType === QuestionType.ImageChoiceGrid ||
        questionType === QuestionType.HybridGrid;

    // For hybrid grid both choice and column will exist
    const choiceOrColumnHasIndexExist = gridRenderedColumnIndex || (choice && choice.index >= 0);

    if (useAriaLabelledBy && isChoiceOrHybridGridType) {
        if (choiceOrColumnHasIndexExist) {
            ariaAttributes["aria-labelledby"] = getAriaLabelledByFromId(
                question,
                questionId,
                `${questionType.toString()}_CHOICEOPTION`,
                (gridRenderedColumnIndex ?? 0) + 1,
                gridRenderedRowIndex + 1,
                (choice.index ?? 0) + 1, // In grid question we have sub-question type so required column level index and choice level index
            );
        }
    } else if (useAriaLabel) {
        const choiceAriaText = removeHtmlTags(translate(choice, "TEXT", language));
        if (choiceAriaText) {
            ariaAttributes["aria-label"] = choiceAriaText;
        }
    }
    const { elementReference, onKeyDown } = useKeyboardNavigation(
        choice.elementId,
        variable,
        question,
        ChoiceGridRendererNavigation,
        onSelect,
    );

    return (
        <div
            id={`${choice.elementId}_INPUT`}
            ref={elementReference}
            role="radio"
            aria-checked={checked}
            {...ariaAttributes}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onFocus={() => setHover(true)}
            onBlur={() => setHover(false)}
            onClick={() => onSelect()}
            onMouseOver={() => setHover(true)}
            onMouseOut={() => setHover(false)}
            className={classnames(
                classes.radio,
                gridRendering && classes.radioGridCell,
                gridRendering && checked && classes.radioGridCellChecked,
            )}
        >
            <RadioIcon
                checked={checked}
                disabled={disabled}
                hover={hover}
                backgroundLess={gridRendering}
            />
        </div>
    );
});

export default RadioRenderer;
